import useTranslator from "../hooks/useTranslator";
import { useContext, useState, useEffect } from "react";
import LandLordContext from "../context/landlord/LandLordContext";
import AuthContext from "../context/auth/AuthContext";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "../helper/common";
import Loader from "./Loader";
import { WORKSPACE_ROLES_CAPABILITIES } from "../Utils/workspaceRoleCapabilities";

const EMPTY_BILLING = {
  company_name: "",
  full_name: "",
  tax_id: "",
  country: "",
  city: "",
  address: "",
  postal_code: "",
};

const TopUpCreditModal = ({ open, setOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { _t } = useTranslator();
  const { addCredit, getPricingPlans } = useContext(LandLordContext);
  const { user } = useContext(AuthContext);
  const { workspaceId } = useParams();

  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [autoTopUp, setAutoTopUp] = useState(false);
  const [requestInvoice, setRequestInvoice] = useState(false);
  const [billingType, setBillingType] = useState("business");
  const [billingData, setBillingData] = useState(EMPTY_BILLING);
  const [creditPlans, setCreditPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPricingPlans = async () => {
    try {
      setLoading(true);
      const response = await getPricingPlans();
      const data = response?.data || response;
      if (data) {
        const formattedPlans = data.map((plan) => ({
          id: plan.id || plan._id,
          credits: plan.credits,
          views: plan.views,
          pricePerProfile: plan.pricePerProfile,
          price: plan.price,
          badge: plan.badge ? _t("mostPopular") : null,
          isEnterprise: plan.isEnterprise || plan.type === "enterprise",
        }));
        setCreditPlans(formattedPlans);
        if (formattedPlans.length > 0 && !selectedPlan) {
          const featuredPlan =
            formattedPlans.find((p) => p?.badge) || formattedPlans[0];
          setSelectedPlan(featuredPlan.id);
        }
      }
    } catch (err) {
      console.log(err);
      toast.error("Error while fetching pricing plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchPricingPlans();
      const saved = user?.current_workspace?.billing_details;
      if (saved) {
        setBillingType(saved.billing_type || "business");
        setBillingData({
          company_name: saved.company_name || "",
          full_name: saved.full_name || "",
          tax_id: saved.tax_id || "",
          country: saved.country || "",
          city: saved.city || "",
          address: saved.address || "",
          postal_code: saved.postal_code || "",
        });
      }
    }
  }, [open]);

  const handleAddCredit = () => {
    setButtonDisabled(true);
    addCredit({
      planId: selectedPlan,
      autoTopUp,
      requestInvoice,
      ...(requestInvoice && {
        billing: {
          billing_type: billingType,
          ...(billingType === "business"
            ? {
                company_name: billingData.company_name,
                tax_id: billingData.tax_id,
              }
            : { full_name: billingData.full_name }),
          country: billingData.country,
          city: billingData.city,
          address: billingData.address,
          postal_code: billingData.postal_code,
        },
      }),
    })
      .then((res) => {
        const data = res.success_res;
        window.location.replace(data?.paymentUrl);
      })
      .catch((err) => {
        console.log(err);
        toast.error("Error while adding credits");
      })
      .finally(() => {
        setButtonDisabled(false);
      });
  };

  const close = () => {
    setSelectedPlan(null);
    setRequestInvoice(false);
    setOpen();
  };

  const handleCreditHistory = () => {
    navigate(`/${workspaceId}/credits-ledger`);
    close();
  };

  const handleContactSales = () => {
    window.open("https://skortorent.com/?chat", "_blank");
  };

  const handleEditBillingDetails = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(location.search);
    params.set("openEditableTab", "billing");
    navigate(
      { pathname: location.pathname, search: `?${params.toString()}` },
      { replace: true }
    );
    close();
  };

  const setField = (key) => (e) =>
    setBillingData((d) => ({ ...d, [key]: e.target.value }));

  const numericOnlyOnKeyDown = (e) => {
    const allowed = [
      "Backspace",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Delete",
      "Enter",
    ];
    if (!/[0-9]/.test(e.key) && !allowed.includes(e.key)) e.preventDefault();
  };

  const postalCodeValid =
    billingData.postal_code.trim() === "" ||
    billingData.postal_code.trim().length === 5;

  const billingComplete = (() => {
    if (!requestInvoice) return true;
    const {
      company_name,
      full_name,
      tax_id,
      country,
      city,
      address,
      postal_code,
    } = billingData;
    const baseOk =
      [country, city, address].every((v) => v.trim() !== "") &&
      postal_code.trim().length === 5;
    return billingType === "business"
      ? baseOk && company_name.trim() !== "" && tax_id.trim() !== ""
      : baseOk && full_name.trim() !== "";
  })();

  const filteredPlans = autoTopUp
    ? creditPlans.filter((plan) => !plan.isEnterprise)
    : creditPlans;

  return (
    open && (
      <div
        className={`modal wrapper-modal fade top-up-credit-modal top-up-credit-modal--open ${
          open ? "show d-block" : ""
        }`}
        onClick={close}
      >
        <div
          className="modal-dialog mx-auto"
          role="document"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content top-up-credit-modal__content">
            <button
              type="button"
              className="top-up-credit-modal__close"
              onClick={close}
              aria-label="Close"
            />

            <div className="top-up-credit-modal__hero">
              <div className="top-up-credit-modal__coin" aria-hidden="true">
                <img src="/images/credit-point1.svg" />
              </div>
              <h5 className="top-up-credit-modal__title">
                {_t("topUpCredits")}
              </h5>
              <p className="top-up-credit-modal__subtitle">
                5 {_t("creditsWillBeUsedToViewEachTenantProfile")}
              </p>
            </div>

            {loading ? (
              <div
                className="top-up-credit-modal__loading"
                style={{ position: "relative" }}
              >
                <Loader />
              </div>
            ) : (
              <div className="top-up-credit-modal__body">
                <div className="top-up-credit-modal__plans">
                  {filteredPlans.map((plan) => {
                    const isSelected = selectedPlan === plan.id;

                    return (
                      <button
                        key={plan.id}
                        type="button"
                        className={`top-up-credit-modal__plan ${
                          isSelected
                            ? "top-up-credit-modal__plan--selected"
                            : ""
                        }`}
                        onClick={() => setSelectedPlan(plan.id)}
                      >
                        {plan.badge && (
                          <span className="top-up-credit-modal__badge">
                            {plan.badge}
                          </span>
                        )}
                        <span
                          className={`top-up-credit-modal__radio ${
                            isSelected
                              ? "top-up-credit-modal__radio--selected"
                              : ""
                          }`}
                        />
                        <span className="top-up-credit-modal__plan-copy">
                          <strong className="top-up-credit-modal__plan-title">
                            {plan.credits} {_t("credits")}
                          </strong>
                          <span className="top-up-credit-modal__plan-meta">
                            x{plan.views} {_t("profileViews")} ·{" "}
                            {plan.pricePerProfile}/{_t("perProfile")}
                          </span>
                        </span>
                        <strong className="top-up-credit-modal__plan-price">
                          € {plan.price}
                        </strong>
                      </button>
                    );
                  })}

                  {!autoTopUp ? (
                    <button
                      type="button"
                      className="top-up-credit-modal__plan top-up-credit-modal__plan--enterprise"
                      onClick={handleContactSales}
                    >
                      <span className="top-up-credit-modal__radio" />
                      <span className="top-up-credit-modal__plan-copy">
                        <strong className="top-up-credit-modal__plan-title">
                          {_t("enterprise")}
                        </strong>
                        <span className="top-up-credit-modal__plan-meta">
                          {_t("customVolume")} ·{" "}
                          <span className="top-up-credit-modal__sales-link">
                            {_t("contactSales")}
                          </span>
                        </span>
                      </span>
                      <span className="top-up-credit-modal__arrow">→</span>
                    </button>
                  ) : null}
                </div>

                {/* Auto top-up */}
                <div className="top-up-credit-modal__auto-top-up">
                  <div className="top-up-credit-modal__auto-copy">
                    <strong>{_t("autoTopUp")}</strong>
                    <span>{_t("autoTopUpDescription")}</span>
                  </div>
                  <button
                    type="button"
                    className={`top-up-credit-modal__switch ${
                      autoTopUp ? "top-up-credit-modal__switch--active" : ""
                    }`}
                    onClick={() =>
                      setAutoTopUp((currentValue) => !currentValue)
                    }
                    aria-pressed={autoTopUp}
                    aria-label="Toggle auto top-up"
                  >
                    <span className="top-up-credit-modal__switch-thumb" />
                  </button>
                </div>

                <div className="top-up-credit-modal__divider" />

                {/* Request invoice row */}
                <div className="top-up-credit-modal__invoice-row">
                  <label className="top-up-credit-modal__invoice">
                    <input
                      type="checkbox"
                      className="top-up-credit-modal__invoice-check"
                      checked={requestInvoice}
                      onChange={() => setRequestInvoice((v) => !v)}
                    />
                    <span className="top-up-credit-modal__invoice-label">
                      {_t("requestInvoice")}
                    </span>
                  </label>

                  {requestInvoice && (
                    <div className="top-up-credit-modal__billing-toggle">
                      <button
                        type="button"
                        className={`top-up-credit-modal__billing-opt ${
                          billingType === "private"
                            ? "top-up-credit-modal__billing-opt--active"
                            : ""
                        }`}
                        onClick={() => setBillingType("private")}
                      >
                        {_t("private")}
                      </button>
                      <button
                        type="button"
                        className={`top-up-credit-modal__billing-opt ${
                          billingType === "business"
                            ? "top-up-credit-modal__billing-opt--active"
                            : ""
                        }`}
                        onClick={() => setBillingType("business")}
                      >
                        {_t("business")}
                      </button>
                    </div>
                  )}
                </div>

                {/* Billing form */}
                {requestInvoice && (
                  <div className="top-up-credit-modal__billing-section">
                    {WORKSPACE_ROLES_CAPABILITIES?.manageBilling?.includes(
                      user?.current_workspace_role
                    ) && (
                      <div className="top-up-credit-modal__billing-section-header">
                        <a
                          href="#"
                          className="top-up-credit-modal__edit-billing"
                          onClick={handleEditBillingDetails}
                        >
                          {_t("editBillingDetails")}
                        </a>
                      </div>
                    )}

                    <div className="top-up-credit-modal__billing-grid">
                      {billingType === "business" ? (
                        <>
                          <div className="forminput customInput mb-0">
                            <label>{_t("companyName")}</label>
                            <input
                              className="modal-input"
                              type="text"
                              value={billingData.company_name}
                              onChange={setField("company_name")}
                              placeholder={_t("enterCompanyName")}
                            />
                          </div>
                          <div className="forminput customInput mb-0">
                            <label>{_t("taxId")}</label>
                            <input
                              className="modal-input"
                              type="text"
                              value={billingData.tax_id}
                              onChange={setField("tax_id")}
                              placeholder={_t("enterTaxId")}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="forminput customInput mb-0 col-span-2">
                          <label>{_t("fullName")}</label>
                          <input
                            className="modal-input"
                            type="text"
                            value={billingData.full_name}
                            onChange={setField("full_name")}
                            placeholder={_t("enterFullName")}
                          />
                        </div>
                      )}
                      <div className="forminput customInput mb-0">
                        <label>{_t("country")}</label>
                        <input
                          className="modal-input"
                          type="text"
                          value={billingData.country}
                          onChange={setField("country")}
                          placeholder={_t("enterCountry")}
                        />
                      </div>
                      <div className="forminput customInput mb-0">
                        <label>{_t("city")}</label>
                        <input
                          className="modal-input"
                          type="text"
                          value={billingData.city}
                          onChange={setField("city")}
                          placeholder={_t("enterCity")}
                        />
                      </div>
                      <div
                        className="forminput customInput mb-0"
                        style={{ maxHeight: "fit-content" }}
                      >
                        <label>{_t("address")}</label>
                        <input
                          className="modal-input"
                          type="text"
                          value={billingData.address}
                          onChange={setField("address")}
                          placeholder={_t("enterAddress")}
                        />
                      </div>
                      <div className="top-up-credit-modal__billing-field">
                        <div className="forminput customInput mb-0">
                          <label>{_t("postalCode")}</label>

                          <input
                            className={`modal-input${
                              !postalCodeValid
                                ? " top-up-credit-modal__billing-input--error"
                                : ""
                            }`}
                            type="text"
                            value={billingData.postal_code}
                            onChange={setField("postal_code")}
                            onKeyDown={numericOnlyOnKeyDown}
                            maxLength={5}
                            placeholder={_t("enterPostalCode")}
                          />
                        </div>

                        {!postalCodeValid && (
                          <span className="top-up-credit-modal__billing-field-error">
                            {_t("postalCodeMustBe5Digits")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="top-up-credit-modal__actions">
                  <button
                    type="button"
                    onClick={handleCreditHistory}
                    className="top-up-credit-modal__button"
                  >
                    {_t("creditHistory")}
                  </button>
                  <button
                    type="button"
                    onClick={handleAddCredit}
                    className="top-up-credit-modal__button top-up-credit-modal__button--primary btnNext"
                    disabled={buttonDisabled || loading || !billingComplete}
                  >
                    {buttonDisabled ? _t("redirecting") : _t("buy")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  );
};

export default TopUpCreditModal;
